import { useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
  getDefaultValuesForForm,
  ButtonContainer,
} from '@product-framework/index';
import {
  Breadcrumb,
  FORM_ACTION,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { useNavigate, useParams } from 'react-router-dom';
import { createSchema, editSchema } from '../../schemas';
import validationSchema from './validations';
import { FORM_STORE_NAME } from '../../store';
import {
  getBrandDataById,
  preProcessRequestData,
  preProcessFetchData,
} from './lib';

const FORM_ID = 'MAINTENANCE_BRAND_FORM';
const PERMISSION_SP = 'BRAND';

function BrandMaintenance({ action = FORM_ACTION.CREATE }) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, put, get, getApiUrl } = useDataProvider(provider);
  const store = useStore();
  const { id: pageId = null } = useParams();

  const postApiUrl = `${getApiUrl()}/sxcl/v1/brands`;
  const updateApiUrl = `${getApiUrl()}/sxcl/v1/brands/${pageId}`;

  const isEditForm = action === FORM_ACTION.EDIT;
  const schema = isEditForm ? editSchema : createSchema;

  const params = {
    post,
    get,
    getApiUrl,
    formatMessage,
  };

  const fetchDefaultValues = useCallback(async () => {
    try {
      if (isEditForm) {
        return getBrandDataById(params, pageId);
      }
      return {
        data: {
          ...getDefaultValuesForForm({
            schema,
          }),
        },
        errors: null,
      };
    } catch (err) {
      return {
        data: null,
        errors: [{ message: err.message }],
      };
    }
  }, [action]);

  const onSubmitCreateForm = async data => {
    const requestData = preProcessRequestData(data);
    const response = await post(postApiUrl, { ...requestData });

    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'BRAND_CREATE_SUCCESS',
          defaultMessage: 'Brand was created successfully',
        }),
      );
      return {
        nextValues: response?.data,
        errors: null,
        callback: () => navigate(`/brands/edit/${response?.data?.id}`),
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const onSubmitEditForm = async data => {
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'BRAND_UPDATE_SUCCESS',
          defaultMessage: 'Brand was saved successfully',
        }),
      );
      return {
        nextValues: preProcessFetchData(response?.data),
        errors: null,
      };
    }
    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const onSubmit = async data => {
    try {
      if (isEditForm) {
        return onSubmitEditForm(data);
      }
      return onSubmitCreateForm(data);
    } catch (err) {
      return {
        nextValues: data,
        errors: [{ message: err.message }],
      };
    }
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'BRANDS_BREADCRUMB',
        defaultMessage: 'Brands',
      }),
      icon: IconLibrary.Brands,
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/brands');
      },
    },
    isEditForm
      ? {
          name: formatMessage({
            id: 'EDIT_BRAND_EDIT_BREADCRUMB',
            defaultMessage: 'Edit Brand',
          }),
          icon: IconLibrary.Brand,
          onClick: undefined,
        }
      : {
          name: formatMessage({
            id: 'CREATE_BRAND_BREADCRUMB',
            defaultMessage: 'Create Brand',
          }),
          icon: IconLibrary.Brand,
          onClick: undefined,
        },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'EDIT_BRANDS_STACKABLE_BUTTON',
        defaultMessage: 'List Brands',
      }),
      Icon: IconLibrary.Brands,
      variant: 'outlined',
      onClick: () => {
        store.uiState[FORM_STORE_NAME].set({});
        navigate('/brands');
      },
    },
  ];

  if (isEditForm) {
    stackablePageButtons.unshift({
      label: formatMessage({
        id: 'CREATE_BRANDS_STACKABLE_BUTTON',
        defaultMessage: 'Create Brand',
      }),
      permissionName: 'CAN_CREATE_EDIT_MAINTENANCE_PAGES',
      Icon: IconLibrary.Brand,
      variant: 'outlined',
      onClick: () => navigate('/brands/create'),
    });
  }

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={
          isEditForm
            ? formatMessage({
                id: 'BRAND_EDIT_HEADING',
                defaultMessage: 'Edit Brand',
              })
            : formatMessage({
                id: 'BRAND_CRE_HEADING',
                defaultMessage: 'Create Brand',
              })
        }>
        <Box>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={schema}
            validationSchema={validationSchema(formatMessage, isEditForm)}
            fetchDefaultValues={fetchDefaultValues}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                {formatMessage({
                  id: 'BRAND_CREATE_SAVE_BUTTON',
                  defaultMessage: 'Save Brand',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => formRef.current?.reset({})}>
                {formatMessage({
                  id: 'BRAND_CREATE_RESET_BUTTON',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(BrandMaintenance);
